import React, {useEffect, useMemo, useState} from 'react';
import {
  fetchTableData,
  ICompetitorOdds,
  IOutrightOddsResponse,
  IOutrightOddsWidgetResponse
} from "../../../api/outright-odds";
import OutrightOddsTableMobile from "../../../components/Sports/OutRightOdds/OutrightOddsTableMobile";
import OutrightOddsTableDesktop from "../../../components/Sports/OutRightOdds/OutrightOddsTableDesktop";
import {applyCustomStyling} from '../../../utils/helpers'
import {setUpTranslations} from '../../../utils/translations';
import ComponentWrapper from '../../MatchCenter/ComponentWrapper';
import marketTypes, {getMarketTypeFullName} from '../../../utils/market-types';
import {IWidgetAvailableMarket} from "../../../utils/types";
import {getWidgetProviders, setWidgetProviders} from '../../../utils/widget-providers';
import {_t} from '../../../utils/translations';

interface IProps {
  widgetId: number;
  isMobile: boolean;
  componentName: string
  competitionId: number | null;
  listRangeFrom?: number | null;
  listRangeTo?: number | null;
  marketTypeIds?: number[] | null;
}

const OutrightOdds: React.FC<IProps> = ({
                                          widgetId,
                                          isMobile,
                                          competitionId,
                                          componentName,
                                          listRangeFrom = null,
                                          listRangeTo = null,
                                          marketTypeIds = null,
                                        }) => {

  const defaultMarketId = marketTypeIds && marketTypeIds.length > 0 ? marketTypeIds[0] : marketTypes.MT_TW;
  const defaultAvailableMarket = useMemo(() => {
    return {
      value: 0,
      title: getMarketTypeFullName((defaultMarketId)),
      marketTypeId: defaultMarketId,
      line: 0
    }
  }, [defaultMarketId]);

  const [loading, setLoading] = useState<boolean>(true)
  const [failedTableData, setFailedTableData] = useState<boolean>(false)
  const [outrightOddsResponse, setOutrightOddsResponse] = useState<IOutrightOddsResponse | null>(null)
  const [widget, setWidget] = useState<IOutrightOddsWidgetResponse | null>(null)
  const [availableMarkets, setAvailableMarkets] = useState<IWidgetAvailableMarket[]>([]);
  const [selectedMarketType, setSelectedMarketType] = useState<IWidgetAvailableMarket>(defaultAvailableMarket)


  useEffect(() => {
    setLoading(true)
    setFailedTableData(false)

    fetchTableData({
      widgetId: widgetId,
      competitionId: competitionId,
      marketTypeId: selectedMarketType.marketTypeId,
      line: selectedMarketType.line,
    }).then((r: IOutrightOddsResponse) => {
      if (r.success) {
        // Apply custom styling functionality
        applyCustomStyling(r.widget.widgetStyles);

        // Save translation to translations storage
        setUpTranslations(r.widget.translations);
        setOutrightOddsResponse(r)
        setWidget(r.widget)

        // Set widgetProviders if it to set
        if (getWidgetProviders().length === 0) {
          setWidgetProviders(r.widget.widgetProviders);
        }

        setAvailableMarkets((oldAvailableMarkets) => {
          let widgetAvailableMarkets = marketTypeIds && marketTypeIds.length > 0
            ? r.widget.availableMarkets.filter(availableMarket => marketTypeIds.includes(availableMarket.marketTypeId))
            : r.widget.availableMarkets;
          if (!widgetAvailableMarkets || widgetAvailableMarkets.length === 0) {
            let availableMarket = null
            if (r.competitorOdds.length > 0) {
              availableMarket = r.widget.availableMarkets.find((availableMarket) => {
                return availableMarket.marketTypeId === r.competitorOdds[0].outRightOddsData.odds[0].market?.marketTypeId
              })
            }

            widgetAvailableMarkets = [availableMarket ?? defaultAvailableMarket]
          }
          //Change the values according to the index of each market after filtering
          return widgetAvailableMarkets.map(function (availableMarketType, index) {
            return {
              ...availableMarketType,
              value: index
            }
          });
        })

        setLoading(false)
      } else {
        setOutrightOddsResponse(null)
        setLoading(false)
      }
    }).catch(() => {
      setFailedTableData(true)
      setLoading(false)
    });
  }, [widgetId, competitionId, defaultAvailableMarket, marketTypeIds, selectedMarketType.line, selectedMarketType.marketTypeId])

  const getCompetitorOdds = (): ICompetitorOdds[] => {
    if (!outrightOddsResponse?.competitorOdds || outrightOddsResponse?.competitorOdds.length === 0) {
      return [];
    }

    const startIndex: number = listRangeFrom && listRangeFrom > 0 ? listRangeFrom - 1 : 0;
    const endIndex: number | undefined = listRangeTo && listRangeTo > 0 ? listRangeTo : undefined;
    return outrightOddsResponse?.competitorOdds.slice(startIndex, endIndex);
  }
  if (!failedTableData && ((widget && getCompetitorOdds().length > 0) || loading)) {
    return (
      widget && <ComponentWrapper
        title={componentName}
        isMobile={isMobile}
        children={(
          <>
            {isMobile
              ? <OutrightOddsTableMobile
                competitorOdds={getCompetitorOdds()}
                widget={widget}
                loading={loading}
                listRangeFrom={listRangeFrom}
                listRangeTo={listRangeTo}
                availableMarkets={availableMarkets}
                changedMarketType={(newSelectedMarketType: string | number): void => {
                  setSelectedMarketType(function (oldSelectedMarketType) {
                    return availableMarkets[newSelectedMarketType]
                  });
                }}
              />
              : <OutrightOddsTableDesktop
                competitorOdds={getCompetitorOdds()}
                widget={widget}
                loading={loading}
                listRangeFrom={listRangeFrom}
                listRangeTo={listRangeTo}
                availableMarkets={availableMarkets}
                changedMarketType={(newSelectedMarketType: string | number): void => {
                  setSelectedMarketType(function (oldSelectedMarketType) {
                    return availableMarkets[newSelectedMarketType]
                  });
                }}
              />
            }
          </>
        )}
      />
    )
  }

  return (
    <div className="outright-odds-nothing-found">
      {_t('Currently there are no odds available.')}
    </div>
  )
}

export default OutrightOdds;
